import { useCallback, useEffect, useMemo, useState } from 'react';

import type { FilterKey } from '@shared/dream-components';

import { useNonRevalidatingFetcher } from '~/hooks/useNonRevalidatingFetcher';
import type { GroupSubscriptionSeatsLoaderData } from '~/routes/__loaders/group_subscription_seats';

export type SeatsQuery = {
  q: string;
  status: FilterKey;
  page: number;
};

const DEFAULT_QUERY: SeatsQuery = { q: '', status: 'all', page: 1 };
const SEARCH_DEBOUNCE_MS = 250;
const DEFAULT_PER_PAGE = 25;

function filterKeyToStatusParam(filter: FilterKey): string | null {
  switch (filter) {
    case 'all':
      return null;
    // "Active" tab = filled + terminating (terminating still holds access until ends_at).
    case 'filled':
      return 'filled,terminating';
    // "Ends at renewal" tab = the terminating subset only.
    case 'terminating':
      return 'terminating';
    default:
      return filter;
  }
}

export type UseGroupSeatsResult = {
  seats: GroupSubscriptionSeatsLoaderData['seats'];
  pagination: GroupSubscriptionSeatsLoaderData['pagination'];
  isLoading: boolean;
  error: string | null;
  query: SeatsQuery;
  setQuery: (next: SeatsQuery | ((prev: SeatsQuery) => SeatsQuery)) => void;
  refetch: () => void;
};

/**
 * Per-group server-side seat fetcher. Search debounces (250ms), status/page
 * fire immediately, `enabled=false` skips the fetch (collapsed cards).
 */
export function useGroupSeats(
  groupId: string,
  { enabled = true, perPage = DEFAULT_PER_PAGE }: { enabled?: boolean; perPage?: number } = {},
): UseGroupSeatsResult {
  const fetcher = useNonRevalidatingFetcher<GroupSubscriptionSeatsLoaderData>();
  const [query, setQuery] = useState<SeatsQuery>(DEFAULT_QUERY);

  const buildUrl = useCallback(
    (q: SeatsQuery) => {
      const params = new URLSearchParams({
        group_subscription_id: groupId,
        page: String(q.page),
        per_page: String(perPage),
      });
      if (q.q.trim()) params.set('q', q.q.trim());
      const statusParam = filterKeyToStatusParam(q.status);
      if (statusParam) params.set('status', statusParam);
      return `/group_subscription_seats?${params.toString()}`;
    },
    [groupId, perPage],
  );

  const [debouncedQ, setDebouncedQ] = useState(query.q);
  useEffect(() => {
    const handle = window.setTimeout(() => setDebouncedQ(query.q), SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(handle);
  }, [query.q]);

  // Depend on the primitive fields, NOT the full `query` object. Otherwise every
  // keystroke creates a new `query` reference → new `effectiveQuery` reference →
  // the fetch effect fires with the un-debounced value, defeating the debounce.
  const effectiveQuery = useMemo<SeatsQuery>(
    () => ({ q: debouncedQ, status: query.status, page: query.page }),
    [debouncedQ, query.status, query.page],
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: fetcher in deps loops forever.
  useEffect(() => {
    if (!enabled) return;
    fetcher.load(buildUrl(effectiveQuery));
  }, [enabled, buildUrl, effectiveQuery]);

  const refetch = useCallback(() => {
    if (!enabled) return;
    fetcher.load(buildUrl(effectiveQuery));
  }, [enabled, fetcher, buildUrl, effectiveQuery]);

  return {
    seats: fetcher.data?.seats ?? [],
    pagination: fetcher.data?.pagination ?? null,
    isLoading: fetcher.state === 'loading',
    error: fetcher.data?.error ?? null,
    query,
    setQuery,
    refetch,
  };
}
